import { Component, OnInit } from '@angular/core';
import { RouterOutlet, Router, ActivatedRoute } from '@angular/router';
import { ScreenLoaderComponent } from './shared/components/load/screen-loader.component';
import { AsyncPipe, NgIf } from '@angular/common';
import { LoaderService } from './shared/services/loader.service';
import { ThemeService } from './shared/services/theme.service';
import { SearchService } from './features/auth/services/search.service';
import { AuthService } from './features/auth/services/auth.service';
import { NavigationService } from './shared/services/navigation.service';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, ScreenLoaderComponent, NgIf, AsyncPipe],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit {
  title = 'ike-now-v2';
  constructor(
    public loaderService: LoaderService,
    private themeService: ThemeService,
    private searchService: SearchService,
    private router: Router,
    private route: ActivatedRoute,
    private authService: AuthService,
    private navigationService: NavigationService
  ) {}

  ngOnInit(): void {
    this.checkSessionAndInitialize();
  }

  private checkSessionAndInitialize(): void {
    const isIOSPWA = /iPad|iPhone|iPod/.test(navigator.userAgent) && (window.navigator as any).standalone;
    
    // Para iOS PWA, dar tiempo extra para que se cargue el localStorage
    if (isIOSPWA) {
      setTimeout(() => {
        this.performSessionCheck();
      }, 100);
    } else {
      this.performSessionCheck();
    }
  }

  private performSessionCheck(): void {
    const isIOSPWA = /iPad|iPhone|iPod/.test(navigator.userAgent) && (window.navigator as any).standalone;
    
    if (isIOSPWA) {
      // Para iOS PWA, usar verificación asíncrona
      this.authService.isSessionActiveForIOSPWA().then(isActive => {
        if (!isActive) {
          // Intentar recuperar tokens de backup
          isActive = this.authService.recoverTokensForIOSPWA();
        }
        
        this.handleSessionCheck(isActive);
      });
    } else {
      // Para otras plataformas, verificación síncrona
      const isActive = this.authService.isSessionActive();
      this.handleSessionCheck(isActive);
    }
  }
  
  private handleSessionCheck(isSessionActive: boolean): void {
    if (isSessionActive) {
      // Si el token está expirado pero hay refresh token, renovar
      if (this.authService.isTokenExpired() && this.authService.canRefreshToken()) {
        this.authService.refreshAccessToken().subscribe({
          next: () => {
            this.redirectToLastRoute();
          },
          error: () => {
            this.initializePWAAndTheme();
          }
        });
      } else if (!this.authService.isTokenExpired()) {
        // Token válido, redirigir a la última ruta o dashboard
        this.redirectToLastRoute();
      } else {
        // No se puede renovar, inicializar normalmente
        this.initializePWAAndTheme();
      }
    } else {
      this.initializePWAAndTheme();
    }
  }

  private redirectToLastRoute(): void {
    const lastRoute = localStorage.getItem('lastRoute');
    if (lastRoute && lastRoute !== '/' && lastRoute !== '/login') {
      this.router.navigate([lastRoute]);
    } else {
      this.router.navigate(['/dashboard']);
    }
    this.initializePWAAndTheme();
  }

  private initializePWAAndTheme(): void {
    // Obtener parámetros de la URL actual
    const urlParams = new URLSearchParams(window.location.search);
    const testIOSPWA = urlParams.get('test-ios-pwa') === 'true';
    const uuidcParam = urlParams.get('uuidc');
    const isPWAStart = urlParams.get('source') === 'pwa';
    
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || testIOSPWA;
    const isIOSPWA = (isIOS && (window.navigator as any).standalone) || testIOSPWA;
    
    // Verificar si hay uuidClient guardado
    const storedUuidClient = localStorage.getItem('uuidClient');
    const backupUuidClient = localStorage.getItem('backup-uuidc');
    const iosPwaUuidc = localStorage.getItem('ios-pwa-uuidc');
    const forceThemeReload = localStorage.getItem('force-theme-reload');

    // El start_url del manifest ya incluye uuidc=2665, no necesitamos restaurar
    
    // Para iOS PWA, aplicar tema guardado inmediatamente
    if (isIOSPWA) {
      this.applyStoredThemeImmediately();
    } else {
      // Para otros casos, cargar desde localStorage normalmente
      this.themeService.loadFromStorage();
    }

    let finalUuidClient: number | null = null;

    if (uuidcParam) {
      // Si viene uuidc en la URL, usarlo
      finalUuidClient = parseInt(uuidcParam);
      if (finalUuidClient > 0) {
        localStorage.setItem('uuidClient', finalUuidClient.toString());
        localStorage.setItem('backup-uuidc', finalUuidClient.toString());
        if (isIOS) {
          localStorage.setItem('ios-pwa-uuidc', finalUuidClient.toString());
        }
      }
    } else if (storedUuidClient && parseInt(storedUuidClient) > 0) {
      // Si no hay uuidc en URL pero sí en localStorage
      finalUuidClient = parseInt(storedUuidClient);
      
      // COMENTADO: No agregar automáticamente el parámetro uuidc a la URL
      // const url = new URL(window.location.href);
      // url.searchParams.set('uuidc', storedUuidClient);
      // if (isPWAStart) {
      //   url.searchParams.delete('source');
      // }
      // window.history.replaceState({}, '', url.toString());
    } else if (iosPwaUuidc && parseInt(iosPwaUuidc) > 0) {
      // Para iOS PWA: usar storage específico
      finalUuidClient = parseInt(iosPwaUuidc);
      localStorage.setItem('uuidClient', finalUuidClient.toString());
      
      // COMENTADO: No agregar automáticamente el parámetro uuidc a la URL
      // const url = new URL(window.location.href);
      // url.searchParams.set('uuidc', finalUuidClient.toString());
      // window.history.replaceState({}, '', url.toString());
    } else if (backupUuidClient && parseInt(backupUuidClient) > 0) {
      // Último recurso: usar backup
      finalUuidClient = parseInt(backupUuidClient);
      localStorage.setItem('uuidClient', finalUuidClient.toString());
      
      // COMENTADO: No agregar automáticamente el parámetro uuidc a la URL
      // const url = new URL(window.location.href);
      // url.searchParams.set('uuidc', finalUuidClient.toString());
      // window.history.replaceState({}, '', url.toString());
    }

    // Cargar tema desde API si tenemos uuidClient
    if (finalUuidClient && finalUuidClient > 0) {
      if (isIOSPWA || forceThemeReload) {
        // Para iOS PWA, cargar inmediatamente y con reintentos
        this.loadThemeForClientWithRetry(finalUuidClient);
        localStorage.removeItem('force-theme-reload');
      } else {
        this.loadThemeForClient(finalUuidClient);
      }
    }
    
    // Para iOS PWA, aplicar tema guardado múltiples veces
    if (isIOSPWA && storedUuidClient) {
      const delays = [100, 300, 500, 1000, 2000];
      delays.forEach(delay => {
        setTimeout(() => {
          this.applyStoredThemeImmediately();
        }, delay);
      });
    }
  }
  
  private applyStoredThemeImmediately(): void {
    const savedTheme = localStorage.getItem('app-theme');
    if (savedTheme) {
      try {
        const theme = JSON.parse(savedTheme);
        const isIOSPWA = /iPad|iPhone|iPod/.test(navigator.userAgent) && (window.navigator as any).standalone;
        if (isIOSPWA) {
          this.themeService.forceApplyThemeForIOSPWA(theme);
        } else {
          this.themeService.applyTheme(theme);
        }
      } catch (e) {
        console.error('Error applying stored theme:', e);
      }
    }
  }

  private loadThemeForClient(uuidClient: number): void {
    this.searchService.loadConfig(uuidClient).subscribe({
      next: (response) => {
        if (response?.success && response.theme) {
          const isIOSPWA = /iPad|iPhone|iPod/.test(navigator.userAgent) && (window.navigator as any).standalone;
          if (isIOSPWA) {
            this.themeService.forceApplyThemeForIOSPWA(response.theme);
          } else {
            this.themeService.applyTheme(response.theme);
          }
        }
      },
      error: (err) => {
        console.error('Error loading theme configuration:', err);
      }
    });
  }
  
  private loadThemeForClientWithRetry(uuidClient: number, attempt: number = 1): void {
    this.searchService.loadConfig(uuidClient).subscribe({
      next: (response) => {
        if (response?.success && response.theme) {
          const isIOSPWA = /iPad|iPhone|iPod/.test(navigator.userAgent) && (window.navigator as any).standalone;
          if (isIOSPWA) {
            this.themeService.forceApplyThemeForIOSPWA(response.theme);
          } else {
            this.themeService.applyTheme(response.theme);
          }
        } else if (attempt < 3) {
          setTimeout(() => {
            this.loadThemeForClientWithRetry(uuidClient, attempt + 1);
          }, 1000 * attempt);
        }
      },
      error: (err) => {
        if (attempt < 3) {
          setTimeout(() => {
            this.loadThemeForClientWithRetry(uuidClient, attempt + 1);
          }, 1000 * attempt);
        }
      }
    });
  }
}
